import React from "react";
import { Barcode, Hand, Clock } from "@phosphor-icons/react";

/**
 * Scan method badge — mirrors the mobile app's audit UX.
 *
 * Base44 contract (2026-09): every /items/scan row carries a `method` field
 * for new scans, and nothing for pre-feature rows. The three states are
 * NEVER collapsed — "unknown method" is NOT the same as "scan". Falling back
 * to SCAN on an unknown row would lie to the auditor, so we show LEGACY.
 *
 *   method === "scan"   → blue SCAN badge   + scanner icon
 *   method === "manual" → amber MANUAL badge + hand icon
 *   undefined/other     → grey LEGACY badge  + clock icon
 */
export default function ScanMethodBadge({ method }) {
  const kind = classify(method);
  const variants = {
    scan: {
      Icon: Barcode,
      label: "SCAN",
      cls: "border-blue-300 bg-blue-50 text-blue-900 dark:bg-blue-950/50 dark:text-blue-200 dark:border-blue-800",
      title: "Scanned from a hardware barcode or webcam reader",
    },
    manual: {
      Icon: Hand,
      label: "MANUAL",
      cls: "border-amber-300 bg-amber-50 text-amber-900 dark:bg-amber-950/50 dark:text-amber-200 dark:border-amber-800",
      title: "Advanced via a UI button, not a barcode scan",
    },
    legacy: {
      Icon: Clock,
      label: "LEGACY",
      cls: "border-zinc-300 bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300 dark:border-zinc-700",
      title: "Pre-feature row — method wasn't recorded",
    },
  };
  const v = variants[kind];
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-md border font-bold uppercase tracking-[0.15em] whitespace-nowrap px-1.5 py-0.5 text-[9px] ${v.cls}`}
      title={v.title}
      data-testid={`scan-method-badge-${kind}`}
    >
      <v.Icon size={10} weight="bold" />
      {v.label}
    </span>
  );
}

/** Normalize whatever came back from the API. Unknown → "legacy". */
export function classify(method) {
  const m = typeof method === "string" ? method.trim().toLowerCase() : "";
  if (m === "scan") return "scan";
  if (m === "manual") return "manual";
  return "legacy";
}

/** Summarize a list of scan rows: { scan: N, manual: N, legacy: N }. */
export function summarizeMethods(scans) {
  const counts = { scan: 0, manual: 0, legacy: 0 };
  for (const s of scans || []) counts[classify(s?.method)] += 1;
  return counts;
}
